import type { Firestore } from "firebase/firestore"
import { collection, doc, getDoc, getDocs, limit, query, setDoc, where } from "firebase/firestore"
import type { User } from "firebase/auth"

export type AccountProfile = Record<string, any>

type EnsureUserProfileDocumentArgs = {
  firestore: Firestore
  authUser: User
}

const AUTH_BACKED_PROFILE_FIELDS = [
  "uid",
  "email",
  "phoneNumber",
  "displayName",
  "name",
  "profileImageUrl",
  "authProvider",
] as const

const isBlank = (value: unknown) => value === undefined || value === null || value === ""

export function getPrimaryAuthProvider(authUser: Pick<User, "providerData">): string {
  const providerId = authUser.providerData?.find((provider) => provider.providerId)?.providerId
  if (!providerId) {
    return "unknown"
  }
  return providerId === "password" ? "local" : providerId
}

export function isRegisteredAuthUser(authUser: Pick<User, "isAnonymous" | "emailVerified" | "providerData"> | null | undefined) {
  if (!authUser || authUser.isAnonymous) {
    return false
  }

  const isEmailProvider = authUser.providerData?.some(
    (provider) => provider.providerId === "password",
  )
  return isEmailProvider ? !!authUser.emailVerified : true
}

export function buildAuthBackedUserProfile(authUser: User): AccountProfile {
  const displayName = authUser.displayName || ""

  return {
    uid: authUser.uid,
    email: authUser.email || "",
    phoneNumber: authUser.phoneNumber || "",
    displayName,
    name: displayName,
    profileImageUrl: authUser.photoURL || "",
    authProvider: getPrimaryAuthProvider(authUser),
  }
}

export function mergeCanonicalUserProfile(
  existingProfile: AccountProfile | null | undefined,
  authProfile: AccountProfile,
): AccountProfile {
  const merged = {
    ...(existingProfile || {}),
  }

  AUTH_BACKED_PROFILE_FIELDS.forEach((field) => {
    if (isBlank(merged[field]) && !isBlank(authProfile[field])) {
      merged[field] = authProfile[field]
    }
  })

  return merged
}

function profilesDifferForAuthBackedFields(profile: AccountProfile, authProfile: AccountProfile) {
  return AUTH_BACKED_PROFILE_FIELDS.some((field) => {
    return isBlank(profile[field]) && !isBlank(authProfile[field])
  })
}

async function findFirstLegacyProfile(
  firestore: Firestore,
  fieldName: "uid" | "email",
  value: string | null,
  canonicalUid: string,
) {
  if (!value) {
    return null
  }

  const snapshot = await getDocs(
    query(collection(firestore, "users"), where(fieldName, "==", value), limit(1)),
  )

  const legacyDoc = snapshot.docs.find((item) => item.id !== canonicalUid) || null
  return legacyDoc ? legacyDoc.data() : null
}

export async function ensureUserProfileDocument({
  firestore,
  authUser,
}: EnsureUserProfileDocumentArgs): Promise<AccountProfile | null> {
  if (!isRegisteredAuthUser(authUser)) {
    return null
  }

  const canonicalRef = doc(firestore, "users", authUser.uid)
  const authProfile = buildAuthBackedUserProfile(authUser)
  const canonicalSnapshot = await getDoc(canonicalRef)

  if (canonicalSnapshot.exists()) {
    const canonicalProfile = canonicalSnapshot.data() || {}
    const mergedProfile = mergeCanonicalUserProfile(canonicalProfile, authProfile)
    if (profilesDifferForAuthBackedFields(canonicalProfile, authProfile)) {
      await setDoc(canonicalRef, mergedProfile, { merge: true })
    }
    return mergedProfile
  }

  const legacyProfile =
    (await findFirstLegacyProfile(firestore, "uid", authUser.uid, authUser.uid)) ||
    (await findFirstLegacyProfile(firestore, "email", authUser.email, authUser.uid))

  const mergedProfile = mergeCanonicalUserProfile(legacyProfile || {}, authProfile)
  await setDoc(canonicalRef, mergedProfile, { merge: true })
  return mergedProfile
}
